import { useCallback, useEffect, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { produce } from 'immer';
import { get, reject } from 'lodash/fp';
import { motion, AnimatePresence, useIsPresent } from 'motion/react';
import { Defaults } from 'app/lib/constants';
import { Button, ButtonSize } from 'app/components/core/Button';
import { EXECUTE_EXPLORE_QUERY } from 'app/store/snowflakeQueries/types.js';
import {
    DOWNLOAD_VIEW_FAILED,
    DOWNLOAD_VIEW_INITIATED,
    PREPARE_DOWNLOAD
} from 'app/store/downloadView/types.js';
import {
    INITIATE_EXPLORATION_DOWNLOAD,
    EXPLORATION_DOWNLOAD_INITIATED,
    EXPLORATION_DOWNLOAD_FAILED
} from 'app/store/exploreData/types.js';
import {
    SHOW_ANALAYZE_SCHEMA_USAGE_FAILURE_NOTIFICATION,
    SHOW_ANALAYZE_SCHEMA_USAGE_SUCCESS_NOTIFICATION
} from 'app/store/schemaUsage/types.js';
import { UNLOAD_JOB_FAILURE_ACTION_TYPE } from 'app/store/jobs/types';
import { NotificationDetailsContainer } from './NotificationDetailsContainer.js';
import {
    AddDatasetStartedNotification,
    DeployTransformationFailedNotification,
    DeployTransformationInitiatedNotification,
    DownloadFailedNotification,
    ExchangeSourceSuccessNotification,
    ExploreDataNotificationContainer,
    GenericErrorNotification,
    GenericNotification,
    GenericSuccessNotification,
    InitiatePrepareDownloadSucceededNotification,
    PrepareDownloadSucceededNotificationContainer,
    TimeoutNotification,
    UploadDatasetNotification,
    AnalyzeSchemaUsageFailureNotification,
    AnalyzeSchemaUsageSuccessNotification,
    AnalyzeSchemaUsageStartedNotification,
    NotebookSqlNodeCreatedNotification,
    UnloadSnowflakeTableFailureNotification,
    SessionRoleUpdatedNotification,
    ConvertToModelFailedNotification,
    ProjectErrorsNotification,
    ExchangeSourceDisconnectedNotification
} from './types/index.js';

import * as styles from './NotificationHub.css';

const cx = classnames.bind(styles);

function getNotificationComponent(type) {
    switch (type) {
        case 'GenericNotification':
            return GenericNotification;
        case 'GenericErrorNotification':
            return GenericErrorNotification;
        case 'GenericSuccessNotification':
            return GenericSuccessNotification;
        case 'FileUploadStarted':
            return AddDatasetStartedNotification;
        case 'UploadDataset_Success':
            return UploadDatasetNotification;
        case DOWNLOAD_VIEW_INITIATED:
        case EXPLORATION_DOWNLOAD_INITIATED:
            return InitiatePrepareDownloadSucceededNotification;
        case PREPARE_DOWNLOAD.SUCCEEDED:
        case INITIATE_EXPLORATION_DOWNLOAD.SUCCEEDED:
            return PrepareDownloadSucceededNotificationContainer;
        case DOWNLOAD_VIEW_FAILED:
        case EXPLORATION_DOWNLOAD_FAILED:
            return DownloadFailedNotification;
        case 'TimeoutNotification':
            return TimeoutNotification;
        case 'DeployTransformationInitiated':
            return DeployTransformationInitiatedNotification;
        case 'DeployTransformationFailed':
            return DeployTransformationFailedNotification;
        case 'ExchangeSourceSuccess':
            return ExchangeSourceSuccessNotification;
        case 'ExchangeSourceDisconnected':
            return ExchangeSourceDisconnectedNotification;
        case EXECUTE_EXPLORE_QUERY.ENDED:
        case EXECUTE_EXPLORE_QUERY.FAILED:
            return ExploreDataNotificationContainer;
        case 'AnalyzeSchemaUsageStarted':
            return AnalyzeSchemaUsageStartedNotification;
        case SHOW_ANALAYZE_SCHEMA_USAGE_FAILURE_NOTIFICATION:
            return AnalyzeSchemaUsageFailureNotification;
        case SHOW_ANALAYZE_SCHEMA_USAGE_SUCCESS_NOTIFICATION:
            return AnalyzeSchemaUsageSuccessNotification;
        case 'NotebookSqlNodeCreated':
            return NotebookSqlNodeCreatedNotification;
        case UNLOAD_JOB_FAILURE_ACTION_TYPE:
            return UnloadSnowflakeTableFailureNotification;
        case 'SessionRoleUpdated':
        case 'SessionWhUpdated':
            return SessionRoleUpdatedNotification;
        case 'ConvertToModelFailed':
            return ConvertToModelFailedNotification;
        case 'ProjectErrors':
            return ProjectErrorsNotification;
        default:
            return null;
    }
}

const VARIANTS = {
    hidden: {
        opacity: 0,
        height: 0
    },
    enter: {
        opacity: 1,
        height: 42,
        transition: {
            ease: 'easeOut',
            duration: 0.3
        }
    },
    leave: {
        opacity: 0,
        height: 0,
        transition: {
            ease: 'easeOut',
            duration: 0.3,
            height: {
                delay: 0.3
            }
        }
    }
};

const MAX_NOTIFICATIONS = 5;
const INITIAL_STATE = {
    isShowingDetails: false,
    currentNotificationId: null,
    allIds: [],
    pending: [],
    visible: []
};

const getId = get('id');

const reducer = produce((draft, action) => {
    switch (action.type) {
        case 'AddNotification':
            if (draft.allIds.includes(action.payload.id)) {
                return;
            }

            // Don't create a notification if we don't have a component for it
            if (!getNotificationComponent(action.payload._type)) {
                return;
            }
            if (draft.visible.length < MAX_NOTIFICATIONS) {
                draft.visible.push(action.payload);
            } else {
                draft.pending.push(action.payload);
            }

            draft.allIds.push(action.payload.id);
            return;

        case 'DismissNotification': {
            const id = action.payload.id;
            draft.visible = reject({ id }, draft.visible);
            requestAnimationFrame(() => action.callback([id]));
            return;
        }

        case 'ClearVisibleNotifications': {
            const ids = draft.visible.map(getId);
            draft.visible = [];
            requestAnimationFrame(() => action.callback(ids));
            return;
        }

        case 'DestroyNotification':
            if (draft.pending.length > 0 && draft.visible.length < MAX_NOTIFICATIONS) {
                draft.visible.push(draft.pending.shift());
            }
            return;

        case 'ShowDetails':
            draft.isShowingDetails = true;
            draft.currentNotificationId = action.payload.id;
            return;

        case 'HideDetails':
            draft.isShowingDetails = false;
            draft.currentNotificationId = null;
            return;

        case 'Reset':
            return INITIAL_STATE;
        default:
            throw new Error('Invalid action type supplied to NotificationHub reducer');
    }
});

function NotificationWrapper({ children, onItemRemoved }) {
    const isPresent = useIsPresent();

    useEffect(() => {
        !isPresent && onItemRemoved();
    }, [isPresent, onItemRemoved]);

    return (
        <motion.div variants={VARIANTS} initial="hidden" animate="enter" exit="leave">
            {children}
        </motion.div>
    );
}

export function NotificationHub({
    testId = null,
    className = null,
    notifications = Defaults.ARRAY,
    onDismiss = Defaults.NOOP,
    ...props
}) {
    const ref = useRef(null);
    const { t } = useTranslation();
    const classes = cx('base', className);
    const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

    useEffect(() => {
        for (const notification of notifications) {
            dispatch({
                type: 'AddNotification',
                payload: notification
            });
        }
    }, [notifications]);

    const handleDismiss = useCallback(
        (id) => {
            dispatch({ type: 'DismissNotification', payload: { id }, callback: onDismiss });
        },
        [onDismiss]
    );

    const handleShowDetails = useCallback((id) => {
        dispatch({ type: 'ShowDetails', payload: { id } });
    }, []);

    const handleHideDetails = useCallback(() => {
        dispatch({ type: 'HideDetails' });
    }, []);

    const handleClear = useCallback(() => {
        dispatch({ type: 'ClearVisibleNotifications', callback: onDismiss });
    }, [onDismiss]);

    const handleItemRemoved = useCallback(() => {
        dispatch({ type: 'DestroyNotification' });
    }, []);

    useEffect(() => {
        try {
            ref.current?.togglePopover(state.visible.length > 0);
        } catch {
            // Ignore error. We might be trying to call this on an element
            // that's being removed from the DOM.
        }
    }, [state.visible.length]);

    return (
        <div data-testid={testId} className={classes} {...props} ref={ref} popover="manual">
            <div className={styles.actions}>
                {state.visible.length > 1 && (
                    <Button size={ButtonSize.SMALL} onClick={handleClear}>
                        {t('components:NotificationHub.clear')}
                    </Button>
                )}
            </div>
            <div className={styles.bars}>
                <AnimatePresence>
                    {state.visible.map((item) => {
                        const { _type, id, details, ...props } = item;
                        const Notification = getNotificationComponent(_type);
                        return (
                            <NotificationWrapper key={id} onItemRemoved={handleItemRemoved}>
                                <Notification
                                    type={_type}
                                    id={id}
                                    onDismiss={handleDismiss}
                                    onShowDetails={handleShowDetails}
                                    hasDetails={Boolean(details)}
                                    {...props}
                                />
                            </NotificationWrapper>
                        );
                    })}
                </AnimatePresence>
            </div>
            {state.isShowingDetails && (
                <NotificationDetailsContainer
                    notificationId={state.currentNotificationId}
                    onClose={handleHideDetails}
                />
            )}
        </div>
    );
}

const NotificationPropType = PropTypes.shape({
    type: PropTypes.string,
    id: PropTypes.string.isRequired,
    message: PropTypes.node
});

NotificationHub.propTypes = {
    /**
     * Emit a `data-testid` prop on the root element of the component. Use this
     * to help automated testing tools find this particular component.
     */
    testId: PropTypes.string,

    /**
     * Set additional CSS classes on the component to extend the default styles.
     */
    className: PropTypes.string,

    /**
     * List of notification objects to render. Maximum 5 will be shown at a time.
     */
    notifications: PropTypes.arrayOf(NotificationPropType),

    /**
     * Callback invoked when the "dimiss" button on an individual Snackbar is
     * activated, when an active auto hide timer runs out, or when the "Clear"
     * button is pressed. Passed a single argument: an array of `id`.
     */
    onDismiss: PropTypes.func
};
